import CloseIcon from '@src/svgs/CloseIcon';
import React, { useCallback, useEffect, useState } from 'react';
import styled, { css, keyframes } from 'styled-components';

import type { ReactNode } from 'react';

// ===========================================================================
// Contextual hint (coach mark) — PLAT-11706.
//
// A small bubble that points at ONE control and says what pressing it does,
// for a first-use moment the control's own label cannot carry ("Dub with AI
// Voice" generates the whole story's dub; nothing says so until you press it).
// It is not a tooltip (it shows without hovering), not a toast (it belongs to
// a place, not a moment) and not a modal (it never blocks). It is positioned
// relative to its anchor, so it follows the control wherever the layout puts
// it, and it goes away for good — for this browser — once the person closes
// it or acts on the control. See "Contextual Hints" in docs/design-system.md.
// ===========================================================================

export type ContextualHintPlacement = 'bottom-end' | 'bottom-start';

const STORAGE_PREFIX = 'arbor.hint.dismissed.';

function storageKey(hintKey: string): string {
  return `${STORAGE_PREFIX}${hintKey}`;
}

/** Whether the person has already closed (or acted on) this hint. */
export function isContextualHintDismissed(hintKey: string): boolean {
  try {
    return window.localStorage.getItem(storageKey(hintKey)) === '1';
  } catch {
    return false;
  }
}

/**
 * Put a hint away for this browser. Hosts call it when the person acts on the
 * control the hint pointed at — the action taught, the lesson is over.
 */
export function dismissContextualHint(hintKey: string): void {
  try {
    window.localStorage.setItem(storageKey(hintKey), '1');
  } catch {
    // Private mode or a full store: the hint simply shows again next time.
  }
}

export interface ContextualHintProps {
  /** Stable id; the dismissal is remembered under it. */
  hintKey: string;
  /** The host's condition for the lesson (e.g. "this language has no dub"). */
  show: boolean;
  /** One or two sentences, sentence case. Name the control as it is labelled. */
  text: string;
  /** Which corner of the anchor the bubble hangs from. Default `bottom-end`. */
  placement?: ContextualHintPlacement;
  /**
   * PLAT-11877 — whether a dismissal is remembered for this browser (the
   * default: a lesson, learnt once). `false` makes it a callout for a state
   * that is worth pointing at every time it is met: closing it hides it for
   * now, and it returns the next time `show` turns on (the host's condition
   * met again — the tab opened again with the state still there).
   */
  remember?: boolean;
  /** Called after the close control puts the hint away. */
  onDismiss?: () => void;
  /**
   * PLAT-12025 — let the wrapper shrink with its row, for a hint around a
   * label that must shorten with "…" when space is tight (the Dubbing
   * column's header in Translation Review). By default the wrapper keeps the
   * width of what it wraps.
   */
  shrink?: boolean;
  testId?: string;
  /** The control the hint points at. */
  children: ReactNode;
}

export function ContextualHint({
  hintKey,
  show,
  text,
  placement = 'bottom-end',
  remember = true,
  onDismiss,
  shrink = false,
  testId = 'contextual-hint',
  children,
}: ContextualHintProps) {
  const [dismissed, setDismissed] = useState(
    () => remember && isContextualHintDismissed(hintKey),
  );
  const handleDismiss = useCallback(() => {
    if (remember) dismissContextualHint(hintKey);
    setDismissed(true);
    onDismiss?.();
  }, [hintKey, onDismiss, remember]);
  // An unremembered callout comes back each time its condition is met anew.
  useEffect(() => {
    if (!remember && !show) setDismissed(false);
  }, [remember, show]);

  const visible = show && !dismissed;
  return (
    <Anchor
      $shrink={shrink}
      // A host that puts the hint away on the control's own action (see
      // `dismissContextualHint`) is read here, after that handler has run, so
      // the bubble leaves on the same click rather than on the next mount.
      onClick={() => {
        if (visible && isContextualHintDismissed(hintKey)) setDismissed(true);
      }}
    >
      {children}
      {visible && (
        <Bubble
          role="note"
          $placement={placement}
          data-testid={testId}
          aria-live="polite"
        >
          <Dot aria-hidden="true" />
          <Text>{text}</Text>
          <Close
            type="button"
            aria-label="Dismiss hint"
            onClick={handleDismiss}
            data-testid={`${testId}-dismiss`}
          >
            <CloseIcon width={10} height={10} />
          </Close>
        </Bubble>
      )}
    </Anchor>
  );
}

export default ContextualHint;

const appear = keyframes`
  from {
    opacity: 0;
    transform: translateY(-4px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const Anchor = styled.span<{ $shrink: boolean }>`
  position: relative;
  display: inline-flex;
  ${({ $shrink }) =>
    $shrink
      ? css`
          flex-grow: 0;
          flex-shrink: 1;
          flex-basis: auto;
          min-width: 0;
          max-width: 100%;
        `
      : css`
          flex: none;
        `}
`;

const ARROW = 8;

const Bubble = styled.div<{ $placement: ContextualHintPlacement }>`
  position: absolute;
  top: calc(100% + ${ARROW + 4}px);
  z-index: 2;
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-sm);
  width: max-content;
  max-width: 280px;
  padding: var(--spacing-sm) var(--spacing-sm) var(--spacing-sm)
    var(--spacing-md);
  background: var(--surface-elevated);
  border: 1px solid var(--border);
  border-radius: var(--radius-button);
  box-shadow: var(--elevation-toast);
  color: var(--foreground);
  font-family: var(--font-primary);
  font-size: var(--text-xs);
  line-height: var(--leading-text-xs);
  text-align: left;
  animation: ${appear} 200ms ease-out;

  @media (prefers-reduced-motion: reduce) {
    animation: none;
  }

  /* The arrow: a rotated square sharing the bubble's fill and border. */
  &::before {
    content: '';
    position: absolute;
    top: -${ARROW / 2 + 1}px;
    width: ${ARROW}px;
    height: ${ARROW}px;
    background: var(--surface-elevated);
    border-top: 1px solid var(--border);
    border-left: 1px solid var(--border);
    transform: rotate(45deg);
  }

  ${props =>
    props.$placement === 'bottom-end'
      ? css`
          right: 0;

          &::before {
            right: 14px;
          }
        `
      : css`
          left: 0;

          &::before {
            left: 14px;
          }
        `}
`;

/* Gold — the colour of the creative controls the hint points at. */
const Dot = styled.span`
  flex: none;
  width: 6px;
  height: 6px;
  margin-top: 5px;
  border-radius: 50%;
  background: var(--accent);
`;

const Text = styled.span`
  flex: 1 1 auto;
`;

const Close = styled.button`
  flex: none;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  margin: -2px -2px -2px 0;
  padding: 0;
  border: none;
  border-radius: 4px;
  background: transparent;
  color: var(--text-muted);
  cursor: pointer;

  &:hover {
    color: var(--foreground);
    background: var(--border);
  }

  &:focus-visible {
    outline: 2px solid var(--ring);
    outline-offset: 1px;
  }
`;
